import React from 'react';
import ReportSection from './ReportSection';
import StatCard from './StatCard';
import {
  describirTendencia,
  etiquetaEstadoBoleta,
  etiquetaEstadoServicio,
  etiquetaMetodoPago,
} from '../../utils/estadisticasReport';

function EstadisticasReport({
  data,
  tendencias,
  deudaPorZona,
  topDeudaClientes,
  filtrosAplicados,
  periodoLabel,
  fechaReporte,
  formatoMoneda,
}) {
  const pagosPorMetodo = data?.pagos?.porMetodo || {};
  const tasas = data?.pagos?.tasas || {};
  const proximasVencer = data?.boletas?.proximasVencer || {};
  const tendenciaLabel = describirTendencia(filtrosAplicados);

  return (
    <div className="estadisticas-report">
      <div className="estadisticas-print-header hidden mb-6 border-b border-slate-200 pb-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">Agua Potable Gustavo André</p>
        <h1 className="text-2xl font-bold text-slate-900">Reporte de estadísticas</h1>
        <p className="text-sm text-slate-600">Periodo: {periodoLabel}</p>
        <p className="text-sm text-slate-600">Generado: {fechaReporte}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Pagos aprobados"
          value={data?.pagos?.aprobados ?? 0}
          subtitle={`Monto cobrado ${formatoMoneda.format(data?.pagos?.montoAprobado ?? 0)}`}
          icon="verified"
          accent="cobros"
        />
        <StatCard
          title="Pagos pendientes"
          value={data?.pagos?.pendientes ?? 0}
          subtitle="Esperando revisión"
          icon="hourglass_bottom"
          accent="atención"
        />
        <StatCard
          title="Pagos rechazados"
          value={data?.pagos?.rechazados ?? 0}
          subtitle="Requieren reenvío"
          icon="block"
          accent="alerta"
        />
        <StatCard
          title="Tasa de aprobación"
          value={`${Math.round((tasas.aprobacion || 0) * 100)}%`}
          subtitle={`Rechazo: ${Math.round((tasas.rechazo || 0) * 100)}%`}
          icon="percent"
          accent="quality"
        />
        <StatCard
          title="Clientes"
          value={data?.usuarios?.totalClientes ?? 0}
          subtitle="Clientes registrados"
          icon="group"
          accent="clientes"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ReportSection
          title="Cobros por método"
          subtitle={`Pagos aprobados - ${periodoLabel}`}
          icon="payments"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {Object.entries(pagosPorMetodo).map(([metodo, info]) => (
              <div
                key={metodo}
                className="p-4 rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50"
              >
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {etiquetaMetodoPago(metodo)}
                </p>
                <p className="text-xl font-semibold text-slate-900 dark:text-white">
                  {info?.cantidad ?? 0}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formatoMoneda.format(info?.monto ?? 0)}
                </p>
              </div>
            ))}
          </div>
        </ReportSection>

        <ReportSection
          title="Medidores"
          subtitle="Cobertura y estado actual de los medidores"
          icon="speed"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              title="Clientes con medidor activo"
              value={data?.medidores?.clientesConMedidorActivo ?? 0}
              subtitle={`Total con historial: ${data?.medidores?.clientesConMedidor ?? 0}`}
              icon="check_circle"
              accent="medidores"
            />
            <StatCard
              title="Clientes sin medidor"
              value={data?.medidores?.clientesSinMedidor ?? 0}
              subtitle="Asignación pendiente"
              icon="priority_high"
              accent="pendiente"
            />
            <StatCard
              title="Medidores dados de baja"
              value={data?.medidores?.medidoresDadosDeBaja ?? 0}
              subtitle={`Rotos: ${data?.medidores?.medidoresRotos ?? 0}`}
              icon="build"
              accent="baja"
            />
            <StatCard
              title="Boletas sin medidor"
              value={data?.boletas?.sinMedidor ?? 0}
              subtitle={`Con medidor: ${data?.boletas?.conMedidor ?? 0}`}
              icon="receipt_long"
              accent="consumo"
            />
            <StatCard
              title="Próximas a vencer"
              value={proximasVencer?.cantidad ?? 0}
              subtitle={`En ${proximasVencer?.diasVencimiento ?? 0} días - ${formatoMoneda.format(proximasVencer?.monto ?? 0)}`}
              icon="event_upcoming"
              accent="alerta"
            />
          </div>
        </ReportSection>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ReportSection
          title="Deuda por zona"
          subtitle={`Boletas pendientes/procesando/vencidas - ${periodoLabel}`}
          icon="map"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm estadisticas-table">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="py-2 pr-4">Zona</th>
                  <th className="py-2 pr-4">Boletas</th>
                  <th className="py-2 pr-4">Deuda</th>
                </tr>
              </thead>
              <tbody>
                {deudaPorZona.map((row, index) => (
                  <tr key={`${row.zonaId ?? 'sin-zona'}-${index}`} className="border-b border-slate-100 dark:border-slate-700">
                    <td className="py-2 pr-4">{row.zonaNombre || 'Sin zona'}</td>
                    <td className="py-2 pr-4">{row.boletas}</td>
                    <td className="py-2 pr-4 font-semibold">
                      {formatoMoneda.format(row.monto || 0)}
                    </td>
                  </tr>
                ))}
                {deudaPorZona.length === 0 && (
                  <tr>
                    <td className="py-3 text-slate-500" colSpan={3}>
                      Sin datos para mostrar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ReportSection>

        <ReportSection
          title="Estados de servicio"
          subtitle="Distribución actual de clientes por estado"
          icon="monitor_heart"
        >
          <div className="space-y-3">
            {data?.usuarios?.estadosServicio &&
              Object.entries(data.usuarios.estadosServicio).map(([estado, cantidad]) => (
                <div
                  key={estado}
                  className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-700 px-4 py-3 bg-slate-50 dark:bg-slate-900/40"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {etiquetaEstadoServicio(estado)}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Clientes en este estado
                    </p>
                  </div>
                  <span className="text-lg font-bold text-slate-900 dark:text-white">
                    {cantidad}
                  </span>
                </div>
              ))}
          </div>
        </ReportSection>
      </div>

      <ReportSection
        title="Boletas"
        subtitle={`Estado de facturación y deuda pendiente - ${periodoLabel}`}
        icon="description"
        className="mb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.boletas?.porEstado &&
            Object.entries(data.boletas.porEstado).map(([estado, info]) => (
              <div
                key={estado}
                className="p-4 rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40"
              >
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {etiquetaEstadoBoleta(estado)}
                </p>
                <p className="text-xl font-semibold text-slate-900 dark:text-white">
                  {info.cantidad}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formatoMoneda.format(info.monto ?? 0)}
                </p>
              </div>
            ))}
        </div>
        <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <p className="text-sm text-amber-800 dark:text-amber-100 font-semibold">
              Deuda pendiente estimada
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-200/80">
              Suma de boletas pendientes, procesando o vencidas
            </p>
          </div>
          <p className="text-lg font-bold text-amber-900 dark:text-amber-50">
            {formatoMoneda.format(data?.boletas?.deudaPendiente ?? 0)}
          </p>
        </div>
      </ReportSection>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ReportSection
          title={`Tendencias de cobros (${tendenciaLabel})`}
          subtitle="Cantidad y monto aprobado por mes y método de pago"
          icon="show_chart"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm estadisticas-table">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="py-2 pr-4">Mes</th>
                  <th className="py-2 pr-4">Aprobados</th>
                  <th className="py-2 pr-4">Monto</th>
                  <th className="py-2 pr-4">Transf.</th>
                  <th className="py-2 pr-4">Efectivo</th>
                  <th className="py-2 pr-4">Tarjeta</th>
                </tr>
              </thead>
              <tbody>
                {tendencias?.pagos?.map((row) => (
                  <tr key={row.mes} className="border-b border-slate-100 dark:border-slate-700">
                    <td className="py-2 pr-4">{row.mes}</td>
                    <td className="py-2 pr-4">{row.pagos_aprobados}</td>
                    <td className="py-2 pr-4 font-semibold">
                      {formatoMoneda.format(row.monto_aprobado || 0)}
                    </td>
                    <td className="py-2 pr-4">
                      {row.transferencia_cant} ({formatoMoneda.format(row.transferencia_monto || 0)})
                    </td>
                    <td className="py-2 pr-4">
                      {row.efectivo_cant} ({formatoMoneda.format(row.efectivo_monto || 0)})
                    </td>
                    <td className="py-2 pr-4">
                      {row.tarjeta_cant} ({formatoMoneda.format(row.tarjeta_monto || 0)})
                    </td>
                  </tr>
                ))}
                {!tendencias?.pagos?.length && (
                  <tr>
                    <td className="py-3 text-slate-500" colSpan={6}>
                      Sin datos de tendencia aún.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ReportSection>

        <ReportSection
          title={`Tendencias de boletas (${tendenciaLabel})`}
          subtitle="Emitidas vs. pagadas por mes"
          icon="timeline"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm estadisticas-table">
              <thead>
                <tr className="text-left text-slate-500 dark:text-slate-400">
                  <th className="py-2 pr-4">Mes</th>
                  <th className="py-2 pr-4">Emitidas</th>
                  <th className="py-2 pr-4">Pagadas</th>
                  <th className="py-2 pr-4">Monto pagado</th>
                </tr>
              </thead>
              <tbody>
                {tendencias?.boletas?.map((row) => (
                  <tr key={row.mes} className="border-b border-slate-100 dark:border-slate-700">
                    <td className="py-2 pr-4">{row.mes}</td>
                    <td className="py-2 pr-4">{row.boletas_emitidas}</td>
                    <td className="py-2 pr-4">{row.boletas_pagadas}</td>
                    <td className="py-2 pr-4 font-semibold">
                      {formatoMoneda.format(row.monto_pagado || 0)}
                    </td>
                  </tr>
                ))}
                {!tendencias?.boletas?.length && (
                  <tr>
                    <td className="py-3 text-slate-500" colSpan={4}>
                      Sin datos de tendencia aún.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ReportSection>
      </div>

      <ReportSection
        title="Top clientes con mayor deuda"
        subtitle={`Ranking por monto adeudado - ${periodoLabel}`}
        icon="trophy"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm estadisticas-table">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-400">
                <th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Padrón</th>
                <th className="py-2 pr-4">Boletas</th>
                <th className="py-2 pr-4">Deuda</th>
              </tr>
            </thead>
            <tbody>
              {topDeudaClientes.map((row) => (
                <tr key={row.usuarioId} className="border-b border-slate-100 dark:border-slate-700">
                  <td className="py-2 pr-4">{row.nombre || 'Sin nombre'}</td>
                  <td className="py-2 pr-4">{row.padron || '-'}</td>
                  <td className="py-2 pr-4">{row.boletas}</td>
                  <td className="py-2 pr-4 font-semibold">
                    {formatoMoneda.format(row.monto || 0)}
                  </td>
                </tr>
              ))}
              {topDeudaClientes.length === 0 && (
                <tr>
                  <td className="py-3 text-slate-500" colSpan={4}>
                    Sin clientes en deuda.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </div>
  );
}

export default EstadisticasReport;
